import React, { useMemo, useCallback, useEffect } from "react";
import {
  getAllKeyPaths,
  buildTreeFromKeyPaths,
  getValueByPath,
  setValueByPath,
  getLanguages,
  countMissingForKey,
  groupHasMissing,
} from "./translationsHelpers";

/**
 * Filter key paths by search (keyPath or value), missingOnly, modulePrefix, showOnlyChanged.
 */
function filterKeyPaths({
  keyPaths,
  translationsJson,
  originalJson,
  search,
  missingOnly,
  modulePrefix,
  showOnlyChanged,
  languages,
}) {
  let list = keyPaths;

  if (modulePrefix) {
    const prefix = `${modulePrefix}.`;
    list = list.filter((kp) => kp === modulePrefix || kp.startsWith(prefix));
  }

  if (search && search.trim()) {
    const q = search.trim().toLowerCase();
    list = list.filter((kp) => {
      if (kp.toLowerCase().includes(q)) return true;
      for (const lang of languages) {
        const v = getValueByPath(translationsJson, lang, kp);
        if (v != null && String(v).toLowerCase().includes(q)) return true;
      }
      return false;
    });
  }

  if (missingOnly) {
    list = list.filter(
      (kp) => countMissingForKey(translationsJson, kp, languages) > 0
    );
  }

  if (showOnlyChanged && originalJson) {
    list = list.filter((kp) => {
      for (const lang of languages) {
        const a = getValueByPath(translationsJson, lang, kp);
        const b = getValueByPath(originalJson, lang, kp);
        const sa = a == null ? "" : String(a);
        const sb = b == null ? "" : String(b);
        if (sa !== sb) return true;
      }
      return false;
    });
  }

  return list;
}

/**
 * Get leaf key paths under a group. Leaf = no other keyPath in the set has this as prefix.
 */
function getLeafKeyPathsUnderGroup(allKeyPaths, groupKeyPath) {
  if (!groupKeyPath) return [];
  const prefix = `${groupKeyPath}.`;
  const under = allKeyPaths.filter((kp) => kp.startsWith(prefix));
  return under.filter(
    (kp) => !under.some((other) => other !== kp && other.startsWith(kp + "."))
  );
}

export function useTranslationsState(initialTranslationsJson, onDirtyChange) {
  const [translationsJson, setTranslationsJson] = React.useState(
    () => JSON.parse(JSON.stringify(initialTranslationsJson || {}))
  );
  const originalRef = React.useRef(
    JSON.parse(JSON.stringify(initialTranslationsJson || {}))
  );

  const initialSerialized = React.useMemo(
    () => JSON.stringify(initialTranslationsJson || {}),
    [initialTranslationsJson]
  );
  React.useEffect(() => {
    const next = JSON.parse(initialSerialized);
    originalRef.current = next;
    setTranslationsJson(next);
  }, [initialSerialized]);

  const languages = useMemo(
    () => getLanguages(translationsJson),
    [translationsJson]
  );

  useEffect(() => {
    const orig = JSON.stringify(originalRef.current);
    const curr = JSON.stringify(translationsJson);
    onDirtyChange(orig !== curr);
  }, [translationsJson, onDirtyChange]);

  const updateValue = useCallback((lang, keyPath, newValue) => {
    setTranslationsJson((prev) =>
      setValueByPath(prev, lang, keyPath, newValue)
    );
  }, []);

  const getMissingCount = useCallback(
    (keyPath) => countMissingForKey(translationsJson, keyPath, languages),
    [translationsJson, languages]
  );

  return {
    translationsJson,
    setTranslationsJson,
    originalTranslationsJson: originalRef.current,
    languages,
    updateValue,
    getMissingCount,
  };
}

export function useFilteredTreeAndSelection({
  translationsJson,
  originalTranslationsJson,
  search,
  missingOnly,
  modulePrefix,
  showOnlyChanged,
}) {
  const languages = useMemo(
    () => getLanguages(translationsJson),
    [translationsJson]
  );
  const allKeyPaths = useMemo(
    () => getAllKeyPaths(translationsJson),
    [translationsJson]
  );

  const filteredKeyPaths = useMemo(
    () =>
      filterKeyPaths({
        keyPaths: allKeyPaths,
        translationsJson,
        originalJson: originalTranslationsJson,
        search,
        missingOnly,
        modulePrefix,
        showOnlyChanged,
        languages,
      }),
    [
      allKeyPaths,
      translationsJson,
      originalTranslationsJson,
      search,
      missingOnly,
      modulePrefix,
      showOnlyChanged,
      languages,
    ]
  );

  const treeNodes = useMemo(
    () => buildTreeFromKeyPaths(filteredKeyPaths),
    [filteredKeyPaths]
  );

  const leafKeyPathsUnderGroup = useCallback(
    (groupKeyPath) =>
      getLeafKeyPathsUnderGroup(filteredKeyPaths, groupKeyPath),
    [filteredKeyPaths]
  );

  const hasFilters =
    !!(search && search.trim()) ||
    !!missingOnly ||
    !!modulePrefix ||
    !!showOnlyChanged;

  return {
    treeNodes,
    filteredKeyPaths,
    leafKeyPathsUnderGroup,
    languages,
    hasFilters,
  };
}
